import { Injectable, inject } from "@angular/core";
import _ from "lodash";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { TypeAheadDTO } from "~app/common/models/common/TypeAheadDTO";
import {
    Produktionsstatus,
    SkogsvardAtgardsGrupp,
    SkogsvardAtgardsTyp,
    SkogsvardBetalningstyp,
    SkogsvardMattslagsTyper,
    VirkeskopAtgardsgrupper
} from "~app/common/models/constants";
import { KartgeometriDTO } from "~app/common/models/karta/KartgeometriDTO";
import { ApiClientService } from "~app/common/services/api-client.service";
import { IVKAtgardKedjaKoppling } from "../models/Atgardskedjor/atgardskedjor-models";
import { Atgard } from "../models/atgard-models";
import { VKAllAtgarderForKontraktDTO } from "../models/kontrakt-atgard-models";
import { TradslagDTO } from "../models/mbf-models";
import { KundKontaktpersonDto } from "../models/skogsvard/kontaktperson-models";
import { KontraktSkogsvardBetalning, SkogsvardstypWithGroupDTO, VKAtgardRojningTradskiktDTO, VKKontraktSkogsvardBetalningDTO } from "../models/skogsvard/skogsvard-models";

const mapBetalningar = (dto: VKKontraktSkogsvardBetalningDTO) => new KontraktSkogsvardBetalning(dto);

@Injectable({
    providedIn: "root"
})
export class SkogsvardService {
    private readonly api = inject(ApiClientService);

    getSkogsvardGrupper(fpv = false): Observable<string[]> {
        return this.api.get<string[]>("virkeskop2/atgard/skogsvard/basdata/skogsvardsgrupper", false, { fpv: fpv });
    }

    getSkogsvardsTyperWithGroups(): Observable<SkogsvardstypWithGroupDTO[]> {
        return this.api.get<SkogsvardstypWithGroupDTO[]>(`virkeskop2/atgard/skogsvard/basdata/skogsvardsgrupper/skogsvardstyper`, false);
    }

    getSkogsvardTyper(grupp: string): Observable<string[]> {
        return this.api.get<string[]>(`virkeskop2/atgard/skogsvard/basdata/skogsvardsgrupp/${grupp}/skogsvardstyper`, false);
    }

    getForyngringssatt(): Observable<string[]> {
        return this.api.get<string[]>(`virkeskop2/atgard/skogsvard/basdata/foryngringssatt`, false);
    }

    getSkogsvardMetoder(typ: string): Observable<string[]> {
        return this.api.get<string[]>(`virkeskop2/atgard/skogsvard/basdata/skogsvardstyp/${typ}/skogsvardsmetoder`, false);
    }

    getTradslag(): Observable<TradslagDTO[]> {
        return this.api.get<TradslagDTO[]>("tradslagsdata", false);
    }

    getRojningTradskikt(atgardId: number): Observable<VKAtgardRojningTradskiktDTO[]> {
        return this.api.get<VKAtgardRojningTradskiktDTO[]>(`virkeskop2/atgard/skogsvard/${atgardId}/tradskikt`);
    }

    getSkogsvardPlanttyper(): Observable<Map<number, string[]>> {
        return this.api.get<Map<number, string[]>>("virkeskop2/atgard/skogsvard/basdata/planttyper", false);
    }

    getSkogsvardsBetalningarForKontrakt(kontraktId?: number): Observable<KontraktSkogsvardBetalning[]> {
        if (!_.isNil(kontraktId)) {
            const betalningar$ = this.api.get<VKKontraktSkogsvardBetalningDTO[]>(`virkeskop2/kontrakt/${kontraktId}/skogsvardbetalningar`);
            return betalningar$.pipe(map(dto =>
                dto.map(mapBetalningar)));
        } else {
            return of([{}] as KontraktSkogsvardBetalning[]);
        }
    }

    getSkogsvardTypDisplayText(skogsvardtypnamn: string): string {
        switch (skogsvardtypnamn) {
            case SkogsvardAtgardsTyp.intermittentMarkberedning:
                return "Int. markberedning";
            case SkogsvardAtgardsTyp.riktadMarkberedning:
                return "Rikt. markberedning";
            case SkogsvardAtgardsTyp.kontinuerligMarkberedning:
                return "Kont. markberedning";
            case SkogsvardAtgardsTyp.dikesrensning:
                return "Markberedning dikesrensning";
            case SkogsvardAtgardsTyp.plantering:
                return "Föryngring Plantering";
            case SkogsvardAtgardsTyp.foryngringViltskydd:
                return "Föryngring Viltskydd";
            case SkogsvardAtgardsTyp.foryngringSadd:
                return "Föryngring Sådd";
            case SkogsvardAtgardsTyp.hjalpplantering:
                return "För. Hjälpplantering";
            case SkogsvardAtgardsTyp.siktrojning:
                return "Röjning siktröjning";
            case SkogsvardAtgardsTyp.nsSiktrojning:
                return "Röjning NS-siktröjning";
            case SkogsvardAtgardsTyp.hyggesrensning:
                return "Röjning hyggesrensning";
            case SkogsvardAtgardsTyp.foryngringsInventering:
                return "Invt. föryngring";
            case SkogsvardAtgardsTyp.plantleverans:
                return SkogsvardAtgardsTyp.plantleverans;
        }
        return skogsvardtypnamn;
    }

    getSkogsvardtypBgColor(skogsvardtypnamn: string): any {
        switch (skogsvardtypnamn) {
            case SkogsvardAtgardsTyp.intermittentMarkberedning:
            case SkogsvardAtgardsTyp.riktadMarkberedning:
            case SkogsvardAtgardsTyp.kontinuerligMarkberedning:
            case SkogsvardAtgardsTyp.dikesrensning:
                return "markberedning-bg";
            case SkogsvardAtgardsTyp.markberedningSadd:
                return "markberedning-sadd-bg";
            case SkogsvardAtgardsTyp.plantering:
            case SkogsvardAtgardsTyp.hjalpplantering:
            case SkogsvardAtgardsTyp.plantleverans:
            case SkogsvardAtgardsTyp.foryngringSadd:
            case SkogsvardAtgardsTyp.foryngringViltskydd:
                return "foryngring-bg";
            case SkogsvardAtgardsTyp.foryngringsInventering:
            case SkogsvardAtgardsTyp.atervaxtkontroll:
                return "inventering-bg";
            case SkogsvardAtgardsTyp.ovrigt:
                return "ovrigt-bg";
            case SkogsvardAtgardsTyp.hyggesrensning:
            case SkogsvardAtgardsTyp.nsSiktrojning:
            case SkogsvardAtgardsTyp.siktrojning:
            case SkogsvardAtgardsTyp.nsRojning:
            case SkogsvardAtgardsTyp.plantrojning:
            case SkogsvardAtgardsTyp.slutrojning:
                return "rojning-bg";
        }
        return "";
    }

    searchForKontraktForSkogsvardsAtgarderTypeAhead(input: string, kontrakttyp: string): Observable<TypeAheadDTO[]> {
        return this.api.get<TypeAheadDTO[]>(`virkeskopskontrakt/searchForKontraktForSkogsvardsAtgarderTypeahead?input=${input}&kontrakttyp=${kontrakttyp}`);
    }

    getAllAtgarderForKontrakt(kontraktid: number): Observable<VKAllAtgarderForKontraktDTO[]> {
        return this.api.get<VKAllAtgarderForKontraktDTO[]>(`virkeskop2/atgard/skogsvard/kontrakt/${kontraktid}`);
    }

    getAllKartgeomtrierForKontrakt(kontraktGuid: string): Observable<KartgeometriDTO[]> {
        return this.api.get<KartgeometriDTO[]>(`virkeskop2/atgard/skogsvard/kontrakt/kartgeometrier/${kontraktGuid}`);
    }

    shouldHandleBelopp(betalning: KontraktSkogsvardBetalning): boolean {
        return this.isBeloppTyp(betalning) &&
            betalning.betalningstyp === SkogsvardBetalningstyp.likvid &&
            betalning.mattslag === SkogsvardMattslagsTyper.timme;
    }

    isBeloppTyp(betalning: KontraktSkogsvardBetalning): boolean {
        return betalning.canChangeMattslag;
    }

    getArbetsorderProduktionsstatus(atgarder: Atgard[], atgardsKedja: IVKAtgardKedjaKoppling[], copyFromGuid?: string): string {

        let atgardId: number;
        if (atgardsKedja) {
            // Hämta avv. åtgärden i kedjan (bör bara vara en och kan vara samma som skogsvard.atgardCopyGuid)
            const avvAtgardKoppling = atgardsKedja?.find(x => x?.atgardsgrupp === VirkeskopAtgardsgrupper.Avverkning);
            atgardId = avvAtgardKoppling?.atgardId;
        } else {
            // Annars hämta från avv.åtgärd
            atgardId = atgarder?.find(x => x.guid === copyFromGuid)?.id;
        }

        const avvAtgard = atgarder?.find(x => x.id === atgardId);

        // Hämta status från kopplad ao
        return avvAtgard?.arbetsorder?.produktionsStatus;
    }

    isValidProduktionsstatusForSiktrojning(arbetsorderProduktionsstatus: string): boolean {
        return (_.isNil(arbetsorderProduktionsstatus) || arbetsorderProduktionsstatus === Produktionsstatus.ProduktionEjPaborjad);
    }

    isSiktrojning(skogsvardTyp: string, skogsvardGrupp: string): boolean {
        return (skogsvardGrupp === SkogsvardAtgardsGrupp.rojning && (skogsvardTyp === SkogsvardAtgardsTyp.nsSiktrojning ||
            skogsvardTyp === SkogsvardAtgardsTyp.siktrojning));
    }

    searchKundTypeAhead(input: string): Observable<TypeAheadDTO[]> {
        return this.api.get<TypeAheadDTO[]>(`virkeskop/kund/typeahead?input=${input}`);
    }

    getKontaktpersonerForKund(kundId: number): Observable<KundKontaktpersonDto[]> {
        return this.api.get<KundKontaktpersonDto[]>(`virkeskop/kund/${kundId}/kontaktpersoner`);
    }
}
